/**
 * The maths behind the plan view: where a coordinate sits on a small canvas, and back again.
 *
 * Kept out of the component so it can be tested without rendering anything, and because the
 * round-trip property — screen to world to screen — is the one thing a drag depends on and the one
 * thing eyeballing a map cannot verify.
 */

/** Metres per degree of latitude. Constant enough at any scale a task can span. */
const METERS_PER_DEG_LAT = 111_320;

/** Metres per degree of longitude AT A GIVEN LATITUDE — they converge towards the poles, and
 *  ignoring that skews every distance on the canvas by `1/cos(lat)`: at 60°N, by a factor of two. */
export function metersPerDegLng(lat: number): number {
  return METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
}

export interface LatLng { lat: number; lng: number }

/**
 * What the canvas is showing: a centre, and how many metres one pixel covers.
 *
 * An equirectangular projection about the centre, not a real map projection. At the scale a field
 * task spans — tens of metres to a few kilometres — the error against a proper projection is far
 * below one pixel, and it buys an invertible two-line transform instead of a dependency.
 */
export interface PlanViewport {
  center: LatLng;
  metersPerPixel: number;
  width: number;
  height: number;
}

export function worldToScreen(p: LatLng, v: PlanViewport): { x: number; y: number } {
  const dLat = p.lat - v.center.lat;
  const dLng = p.lng - v.center.lng;
  const north = dLat * METERS_PER_DEG_LAT;
  const east = dLng * metersPerDegLng(v.center.lat);
  return {
    x: v.width / 2 + east / v.metersPerPixel,
    // Screen y grows downward and north is up, hence the negation. Getting this wrong is a bug that
    // looks like a working map until someone drags north and the pin goes south.
    y: v.height / 2 - north / v.metersPerPixel,
  };
}

export function screenToWorld(pt: { x: number; y: number }, v: PlanViewport): LatLng {
  const east = (pt.x - v.width / 2) * v.metersPerPixel;
  const north = (v.height / 2 - pt.y) * v.metersPerPixel;
  return {
    lat: v.center.lat + north / METERS_PER_DEG_LAT,
    lng: v.center.lng + east / metersPerDegLng(v.center.lat),
  };
}

/**
 * A viewport that holds every target, their tolerance circles, and the pin.
 *
 * The circles matter as much as the points: a 500 m tolerance around a single target is the thing
 * worth seeing, and fitting the point alone would zoom in until the circle filled the screen with
 * no edge visible — and the edge is the whole question.
 *
 * With no targets at all it falls back to a fixed scale around the pin, because there is nothing to
 * fit and an arbitrary zoom is better than a division by zero.
 */
export function fitViewport(
  targets: Array<{ at: LatLng; radiusMeters: number }>,
  pin: LatLng,
  width: number,
  height: number,
): PlanViewport {
  const DEFAULT_METERS_PER_PIXEL = 0.5;
  if (width <= 0 || height <= 0) {
    return { center: pin, metersPerPixel: DEFAULT_METERS_PER_PIXEL, width, height };
  }
  if (!targets.length) {
    return { center: pin, metersPerPixel: DEFAULT_METERS_PER_PIXEL, width, height };
  }

  const lats = [pin.lat, ...targets.flatMap(t => [t.at.lat, t.at.lat])];
  const lngs = [pin.lng, ...targets.flatMap(t => [t.at.lng, t.at.lng])];
  const center = {
    lat: (Math.min(...lats) + Math.max(...lats)) / 2,
    lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
  };

  // Half-extents in METRES from that centre, each target reaching out by its own radius.
  const mPerLng = metersPerDegLng(center.lat);
  let halfEast = Math.abs(pin.lng - center.lng) * mPerLng;
  let halfNorth = Math.abs(pin.lat - center.lat) * METERS_PER_DEG_LAT;
  for (const t of targets) {
    halfEast = Math.max(halfEast, Math.abs(t.at.lng - center.lng) * mPerLng + t.radiusMeters);
    halfNorth = Math.max(halfNorth, Math.abs(t.at.lat - center.lat) * METERS_PER_DEG_LAT + t.radiusMeters);
  }

  // A tenth of margin so nothing sits against an edge, and a floor so a single zero-radius target
  // does not divide by zero and zoom to infinity.
  const MIN_HALF_EXTENT_M = 10;
  const scale = Math.max(
    (Math.max(halfEast, MIN_HALF_EXTENT_M) * 2.2) / width,
    (Math.max(halfNorth, MIN_HALF_EXTENT_M) * 2.2) / height,
  );
  return { center, metersPerPixel: scale, width, height };
}

/**
 * A round number of metres to draw the scale bar at, and how wide that is in pixels.
 *
 * The 1/2/5 progression, the same one the chart axis uses, for the same reason: those are the
 * numbers people read without converting.
 */
export function scaleBarFor(v: PlanViewport): { meters: number; pixels: number } {
  const target = v.width / 4;
  const rawMeters = target * v.metersPerPixel;
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(rawMeters, 1e-6)));
  const normalized = rawMeters / magnitude;
  const nice = normalized < 1.5 ? 1 : normalized < 3.5 ? 2 : normalized < 7.5 ? 5 : 10;
  const meters = nice * magnitude;
  return { meters, pixels: meters / v.metersPerPixel };
}

/** A point `meters` away from `from` on the given compass bearing. Used by the snap chips, which
 *  place the pin relative to a target rather than at an absolute coordinate a teacher never typed. */
export function offsetBy(from: LatLng, meters: number, bearingDegrees: number): LatLng {
  const rad = (bearingDegrees * Math.PI) / 180;
  const north = Math.cos(rad) * meters;
  const east = Math.sin(rad) * meters;
  return {
    lat: from.lat + north / METERS_PER_DEG_LAT,
    lng: from.lng + east / metersPerDegLng(from.lat),
  };
}

/**
 * A grid, in metres, anchored to the WORLD rather than to the canvas.
 *
 * Without tiles there are no landmarks, so nothing on screen says you are moving except the pin
 * itself — and a pin that moves against a blank field looks like a pin that is standing still while
 * everything else does. A world-anchored grid fixes that: the lines slide under the pin as it
 * moves, and they stay put as the fit changes, which is the only cue that says how far.
 *
 * Anchored to a TARGET where there is one, so lines pass through it and distance can be counted in
 * squares from the thing that matters, rather than from an arbitrary point.
 *
 * Spacing is `scaleBarFor`'s own nice number, deliberately: the scale bar is then exactly one
 * square wide, and one label explains both. Two numbers — a bar of 50 m over a grid of 20 — would
 * be two things to hold in your head to read one distance.
 */
export function gridLinesFor(v: PlanViewport, origin: LatLng): {
  spacingMeters: number;
  spacingPixels: number;
  xs: number[];
  ys: number[];
} {
  const { meters: spacingMeters, pixels: spacingPixels } = scaleBarFor(v);
  const anchor = worldToScreen(origin, v);
  const lines = (anchorPx: number, extent: number) => {
    const out: number[] = [];
    if (!(spacingPixels > 0) || !Number.isFinite(spacingPixels) || extent <= 0) return out;
    // Walk out from the anchor in both directions rather than from zero, so a line always lands
    // exactly ON the target however the viewport is positioned.
    const first = anchorPx - Math.ceil(anchorPx / spacingPixels) * spacingPixels;
    // Bounded, because a spacing that rounds to a fraction of a pixel would otherwise fill the
    // canvas with thousands of lines and take the frame with it.
    const MAX_LINES = 200;
    for (let x = first, n = 0; x <= extent && n < MAX_LINES; x += spacingPixels, n += 1) {
      if (x >= 0) out.push(x);
    }
    return out;
  };
  return {
    spacingMeters,
    spacingPixels,
    xs: lines(anchor.x, v.width),
    ys: lines(anchor.y, v.height),
  };
}

/**
 * How far the pin may be dragged from the nearest target.
 *
 * Without a limit the pin runs away: `fitViewport` includes it, so dragging outward zooms out,
 * which makes the next drag cover more ground, which zooms out further. A few seconds of that and
 * the targets are a dot in the middle of an ocean.
 *
 * The limit is a GUARD, not a plausibility check. Being off by kilometres is a real thing a teacher
 * may want to test — a student at the wrong site entirely — so the floor is generous enough that
 * anything short of continental never hits it. What it exists to stop is the pin leaving the planet,
 * and `Reset` is the answer to the milder problem of having zoomed out too far to see anything.
 *
 * Ten times the radius scales with a task that already works at kilometre scale; the floor covers
 * one whose tolerance is a couple of metres, where ten times nothing is still nothing.
 */
const LIMIT_RADIUS_MULTIPLE = 10;
const LIMIT_FLOOR_M = 200_000;

/**
 * Latitude cannot reach the poles, and this is arithmetic rather than taste.
 *
 * `metersPerDegLng` is `cos(lat)`, which is ZERO at 90 degrees — so `screenToWorld` divides by it
 * and every coordinate becomes Infinity. 85 is the conventional stopping point and leaves the pin
 * somewhere no field task has ever been.
 */
const MAX_ABS_LAT = 85;

/** Longitude and latitude brought back onto the globe. The outer guard, applied whether or not the
 *  task has a target to be far from — a pin at latitude 200 is not a place. */
function ontoTheGlobe(p: LatLng): LatLng {
  return {
    lat: Math.min(Math.max(p.lat, -MAX_ABS_LAT), MAX_ABS_LAT),
    // Wrapped rather than clamped: crossing the antimeridian is a real journey, and stopping dead
    // at 180 would be a wall in the middle of the Pacific.
    lng: ((((p.lng + 180) % 360) + 360) % 360) - 180,
  };
}

export function pinLimitMeters(targets: Array<{ radiusMeters: number }>): number {
  const widest = targets.length ? Math.max(...targets.map(t => t.radiusMeters)) : 0;
  return Math.max(widest * LIMIT_RADIUS_MULTIPLE, LIMIT_FLOOR_M);
}

/**
 * `next`, pulled back onto the limit if it has passed it.
 *
 * Measured from the NEAREST target, so a task with several sites lets the pin travel between them
 * however far apart they are — the limit is about running off into nowhere, not about keeping the
 * device near any one place. With no targets at all there is nothing to be far from, so the point
 * passes through.
 */
export function clampToLimit(
  raw: LatLng,
  targets: Array<{ at: LatLng; radiusMeters: number }>,
): LatLng {
  const next = ontoTheGlobe(raw);
  if (!targets.length) return next;
  const limit = pinLimitMeters(targets);
  let best: { at: LatLng; d: number } | null = null;
  for (const t of targets) {
    const mPerLng = metersPerDegLng(t.at.lat);
    const d = Math.hypot(
      (next.lat - t.at.lat) * METERS_PER_DEG_LAT,
      (next.lng - t.at.lng) * mPerLng,
    );
    if (!best || d < best.d) best = { at: t.at, d };
  }
  if (!best || best.d <= limit) return next;
  // Same direction, shorter walk — so a drag past the limit slides along it rather than stopping
  // dead somewhere unrelated to where the pointer went.
  const mPerLng = metersPerDegLng(best.at.lat);
  const bearing = Math.atan2(
    (next.lng - best.at.lng) * mPerLng,
    (next.lat - best.at.lat) * METERS_PER_DEG_LAT,
  );
  return ontoTheGlobe(offsetBy(best.at, limit, (bearing * 180) / Math.PI));
}

/** How close a pointer must land to the arrowhead to mean "swivel" rather than "move". */
export const ARROW_GRAB_PX = 18;

/**
 * How close a pointer must land to the PIN to mean "move" at all.
 *
 * Generous against the pin's drawn radius of 9, because it is a target for a finger as well as
 * a mouse — and because missing it now does nothing rather than doing the wrong thing.
 */
export const PIN_GRAB_PX = 20;

/**
 * WHICH HANDLE A PRESS IS AIMED AT, or neither.
 *
 * There used to be no "neither": anything that was not the arrow grabbed the pin, so a press
 * anywhere on the canvas teleported the position to it, and the whole box advertised
 * `cursor-grab` to say so. Reported as not being able to tell what was a control — the compass
 * arrow is the smaller and more important handle, and it read as part of a surface that moved
 * wherever you clicked.
 *
 * THE ARROW WINS TIES. It is the smaller target and sits out on the ring, so a press near it is
 * almost never meant for the pin underneath — and the pin's own grab radius is wide enough to
 * reach the arrow's at close zoom, which is exactly when the tie happens.
 *
 * Here rather than in the component because it is arithmetic over two points, it is the whole
 * of what a press MEANS, and jsdom has no `PointerEvent` — a test driving the component's
 * handler would be inert and would pass whatever this said.
 */
export function grabTargetAt(
  p: { x: number; y: number },
  handles: {
    pin: { x: number; y: number };
    tip: { x: number; y: number };
    /** The pin is draggable at all — false on a compass-only canvas, which has no position. */
    showPin: boolean;
    /** The heading ring is drawn, so there is an arrow to grab. */
    showRing: boolean;
  },
): 'pin' | 'arrow' | null {
  const { pin, tip, showPin, showRing } = handles;
  if (showRing && Math.hypot(p.x - tip.x, p.y - tip.y) <= ARROW_GRAB_PX) return 'arrow';
  if (showPin && Math.hypot(p.x - pin.x, p.y - pin.y) <= PIN_GRAB_PX) return 'pin';
  return null;
}

